import React, { useEffect, useState } from 'react';
import { dispatchAlertToaster } from 'adapter/in/common/context/Alert';
import { dispatchLayoutsView, useLayoutsView } from 'adapter/in/common/context/Layouts/LayoutsContext';
import { useUseCaseAlerts } from 'adapter/in/common/hooks/useUseCaseAlerts';
import useBusinessInsight from 'adapter/in/common/widget/BusinessInsight/useBusinessInsight';
import useTextBlock from 'adapter/in/common/widget/TextBlock/useTextBlock';
import { isDashboardFrame } from 'adapter/in/common/widget/widgetBuilder';
import { GridLayoutElement } from 'adapter/in/dashboards/components/generic/ReactGrid';
import { isLocalBusinessInsightId } from 'adapter/in/dashboards/components/Grid/localBusinessInsight';
import { isLocalTextBlockId } from 'adapter/in/dashboards/components/Grid/localTextBlock';
import { mapGridElementsToLayout } from 'adapter/in/dashboards/components/Grid/gridUtils';
import { useTextBlockData } from 'adapter/in/reports/contexts/TextBlockContext';
import i18n from 'asset/translation';
import Layout, { LayoutType } from 'domain/entity/layout';
import { ErrorData } from 'domain/types';
import { isLeft } from 'fp-ts/lib/Either';
import { isSome, Option, some } from 'fp-ts/lib/Option';
import { CreateBusinessInsightCriteriaBuilder } from 'use-case/widgets/create/createBusinessInsightCriteria';
import type { CreateBusinessInsightResult } from 'use-case/widgets/create/createBusinessInsightUseCase';
import { CreateTextBlockCriteriaBuilder } from 'use-case/widgets/create/createTextBlockCriteria';
import type { CreateTextBlockResult } from 'use-case/widgets/create/createTextBlockUseCase';
import UpdateLayoutCriteria, { UpdateLayoutCriteriaBuilder } from 'use-case/layout/update/updateLayoutCriteria';

export type SaveReportTemplateLayoutUseCase = {
    dataFromUpdateTemplateLayout: Option<Layout>;
    execute: (criteria: UpdateLayoutCriteria) => void;
    setLayoutsData?: React.Dispatch<React.SetStateAction<Option<Layout[]>>>;
    reset: () => void;
    error: Option<ErrorData>;
    isLoading?: boolean;
};

type UseSaveReportTemplateLayoutOptions = {
    onSaveSuccess?: () => void;
    exitEditModeOnSave?: boolean;
};

type LocalWidgetCreationResult = { localId: string; result: CreateTextBlockResult | CreateBusinessInsightResult };
type LocalBusinessInsightGridElement = GridLayoutElement & { businessInsightType: string };

const hasBusinessInsightType = (element: GridLayoutElement): element is LocalBusinessInsightGridElement =>
    !!element.businessInsightType;

export const useSaveReportTemplateLayout = (
    reportTemplateId: string,
    updateTemplateLayout: SaveReportTemplateLayoutUseCase,
    options: UseSaveReportTemplateLayoutOptions = {}
) => {
    const { dataFromUpdateTemplateLayout, execute, reset, setLayoutsData, error, isLoading } = updateTemplateLayout;
    const { onSaveSuccess, exitEditModeOnSave = true } = options;
    const { setEditLayoutMode, setHasLayoutChanges } = dispatchLayoutsView();
    const { allGridLayouts } = useLayoutsView();
    const { createTextBlock } = useTextBlock();
    const { createBusinessInsight } = useBusinessInsight();
    const { editors } = useTextBlockData();
    const { setErrorAlert } = dispatchAlertToaster();
    const [isSavingLocalWidgets, setIsSavingLocalWidgets] = useState(false);

    const getLocalTextBlockHtml = (textBlockId: string): string =>
        editors?.find((editorWithId) => editorWithId.textBlockId === textBlockId)?.editor?.getHTML() ?? '';

    const saveLayout = (layout: Layout) => {
        execute(UpdateLayoutCriteriaBuilder.create().withLayout(layout).withDashboardId(reportTemplateId).build());
        setLayoutsData?.(some([layout]));
    };

    const createSequentially = <T,>(
        localWidgets: T[],
        createLocalWidget: (localWidget: T) => Promise<LocalWidgetCreationResult>
    ): Promise<LocalWidgetCreationResult[]> => {
        const [firstWidget, ...remainingWidgets] = localWidgets;
        if (!firstWidget) return Promise.resolve([]);

        return remainingWidgets.reduce<Promise<LocalWidgetCreationResult[]>>(
            (previousResultsPromise, localWidget) =>
                previousResultsPromise.then((previousResults) =>
                    createLocalWidget(localWidget).then((creationResult) => [...previousResults, creationResult])
                ),
            createLocalWidget(firstWidget).then((creationResult) => [creationResult])
        );
    };

    const saveLayoutWithPersistedLocalWidgets = async (layoutElements: GridLayoutElement[]) => {
        const localTextBlocks = layoutElements.filter((element) => isLocalTextBlockId(element.i));
        const localBusinessInsights = layoutElements.filter((element) => isLocalBusinessInsightId(element.i));
        const localBusinessInsightsWithType = localBusinessInsights.filter(hasBusinessInsightType);

        if (localTextBlocks.length > 0 && !createTextBlock) {
            setErrorAlert(i18n.t('reports.reportTemplate.layoutSaveError'));
            return;
        }

        if (localBusinessInsights.length > 0 && !createBusinessInsight) {
            setErrorAlert(i18n.t('reports.reportTemplate.layoutSaveError'));
            return;
        }

        if (localBusinessInsightsWithType.length !== localBusinessInsights.length) {
            setErrorAlert(i18n.t('reports.reportTemplate.layoutSaveError'));
            return;
        }

        setIsSavingLocalWidgets(true);

        const createLocalTextBlock = (localTextBlock: GridLayoutElement): Promise<LocalWidgetCreationResult> =>
            createTextBlock(
                CreateTextBlockCriteriaBuilder.create()
                    .withDashboardId(reportTemplateId)
                    .withRichText(getLocalTextBlockHtml(localTextBlock.i))
                    .build()
            ).then((result) => ({
                localId: localTextBlock.i,
                result,
            }));

        const createLocalBusinessInsight = (localBusinessInsight: LocalBusinessInsightGridElement): Promise<LocalWidgetCreationResult> =>
            createBusinessInsight(
                CreateBusinessInsightCriteriaBuilder.create()
                    .withDashboardId(reportTemplateId)
                    .withType(localBusinessInsight.businessInsightType)
                    .build()
            ).then((result) => ({
                localId: localBusinessInsight.i,
                result,
            }));

        const creationResults = [
            ...(await createSequentially(localTextBlocks, createLocalTextBlock)),
            ...(await createSequentially(localBusinessInsightsWithType, createLocalBusinessInsight)),
        ];

        if (creationResults.some(({ result }) => isLeft(result))) {
            setErrorAlert(i18n.t('reports.reportTemplate.layoutSaveError'));
            setIsSavingLocalWidgets(false);
            return;
        }

        const createdWidgetIdsByLocalId = new Map<string, string>();
        creationResults.forEach(({ localId, result }) => {
            if (!isLeft(result)) {
                createdWidgetIdsByLocalId.set(localId, result.right.id);
            }
        });

        const persistedLayoutElements = layoutElements.map((element) => ({
            ...element,
            i: createdWidgetIdsByLocalId.get(element.i) ?? element.i,
        }));

        saveLayout(mapGridElementsToLayout(persistedLayoutElements, LayoutType.A4));
        setIsSavingLocalWidgets(false);
    };

    const handleUpdateLayoutAction = () => {
        const gridLayoutsData = allGridLayouts[LayoutType.A4];

        if (!gridLayoutsData) return;

        const layoutElements = gridLayoutsData.filter((element) => !isDashboardFrame(element.i, element.type));
        const localTextBlocks = layoutElements.filter((element) => isLocalTextBlockId(element.i));
        const localBusinessInsights = layoutElements.filter((element) => isLocalBusinessInsightId(element.i));

        if (localTextBlocks.length === 0 && localBusinessInsights.length === 0) {
            saveLayout(mapGridElementsToLayout(layoutElements, LayoutType.A4));
            return;
        }

        saveLayoutWithPersistedLocalWidgets(layoutElements);
    };

    useEffect(() => {
        if (isSome(dataFromUpdateTemplateLayout)) {
            if (exitEditModeOnSave) {
                setEditLayoutMode(false);
            }
            setHasLayoutChanges(false);
            reset();
            onSaveSuccess?.();
        }
    }, [dataFromUpdateTemplateLayout]);

    useUseCaseAlerts(dataFromUpdateTemplateLayout, error, {
        successMessage: 'reports.reportTemplate.layoutSaveSuccess',
        errorMessage: 'reports.reportTemplate.layoutSaveError',
    });

    return {
        handleUpdateLayoutAction,
        isSaving: isSavingLocalWidgets || !!isLoading,
        hasError: isSome(error),
    };
};
